/**
 * Query keys del dominio Talonarios, segmentadas por sede activa.
 *
 * Keys consistentes y serializables (`docs/frontend_conventions.md`, Datos):
 * `['talonarios', ..., { sede, ... }]`. Incluir la sede en la key **cachea por
 * sede**: al cambiar de sede, listados/grid/historial se recargan (clave
 * distinta) en vez de mostrar los de la sede anterior. La sede activa la inyecta
 * `lib/api` como `X-Sede-Id`; aquí solo entra en la key para segmentar el cache.
 */
import type { TalonarioHistoryQuery, TalonarioMismatchesQuery } from "../types";

/** Raíz de todas las queries de talonarios (para invalidación amplia). */
export const TALONARIOS_ROOT_KEY = ["talonarios"] as const;

/** Key del estado de todos los talonarios de la sede. */
export function talonariosListQueryKey(sedeId: number | null) {
  return ["talonarios", "list", { sede: sedeId }] as const;
}

/** Key del grid del talonario ACTUAL de un trabajador. */
export function talonarioGridQueryKey(sedeId: number | null, worker: string) {
  return ["talonarios", "grid", { sede: sedeId, worker }] as const;
}

/** Key del grid de un rango HISTÓRICO de un trabajador. */
export function talonarioRangeGridQueryKey(
  sedeId: number | null,
  worker: string,
  start: number,
  end: number,
) {
  return ["talonarios", "range-grid", { sede: sedeId, worker, start, end }] as const;
}

/** Key del historial paginado, segmentada por sede + filtros. */
export function talonarioHistoryQueryKey(
  sedeId: number | null,
  query: TalonarioHistoryQuery,
) {
  return [
    "talonarios",
    "history",
    {
      sede: sedeId,
      worker: query.worker,
      action: query.action,
      page: query.page,
      per_page: query.per_page,
    },
  ] as const;
}

/** Key de los descuadres, segmentada por sede + filtros. */
export function talonarioMismatchesQueryKey(
  sedeId: number | null,
  query: TalonarioMismatchesQuery,
) {
  return [
    "talonarios",
    "mismatches",
    {
      sede: sedeId,
      start_date: query.start_date,
      end_date: query.end_date,
      worker: query.worker,
    },
  ] as const;
}

/** Key de las alertas no vistas, segmentada por sede. */
export function talonarioAlertasQueryKey(sedeId: number | null) {
  return ["talonarios", "alertas", { sede: sedeId }] as const;
}
